/**
 * The role gate for the user-management route (epic 103 D8, task 03): the
 * Users screen mounts only for an admin session. It sits INSIDE the
 * `RequireAuth` subtree, so by the time it renders, the mount-time `me` check
 * has already resolved — the two residual states mirror `require-auth.tsx`:
 *
 * - still loading (possible on a remount while `me` is re-resolving): the
 *   same neutral loading frame — never an optimistic render and never a
 *   white screen (#134/#137);
 * - `role !== 'admin'` (a signed-in employee hitting /users directly, or a
 *   signed-out instant where the shell is gone): a defined access-denied
 *   frame — centered card, plain sentence, a link back to `/`. No redirect,
 *   so the URL stays honest and no loop is possible.
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';

export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        role="status"
        className="flex min-h-screen items-center justify-center text-sm text-muted"
      >
        Loading…
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md rounded-lg border border-line bg-card p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">Access denied</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            You don't have access to user management.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block rounded-md border border-line px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-line/40"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
